import React, { useEffect, useState } from "react";
import { Lock, Plus, Trash2, Copy, AlertTriangle, Check, BookOpen, ChevronDown, ChevronUp, ArrowUp } from "lucide-react";
import toast from "react-hot-toast";
import ApiService from "../../services/ApiService";
import { useOrganization } from "@clerk/clerk-react";
import { useLocation, useNavigate } from "react-router-dom";

function maskValue(key) {
  // Display the visible_part directly from the API response
  const visible = key?.visible_part || key?.visiblePart || "";
  if (!visible) return "****…";
  return `${visible}****`;
}

function formatDate(d) {
  if (!d) return "-";
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString();
  } catch {
    return d;
  }
}

function CopyBtn({ text, id, copiedCode, setCopiedCode }) {
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(typeof text === "string" ? text : JSON.stringify(text, null, 2));
        setCopiedCode(id);
        toast.success("Copied!");
        setTimeout(() => setCopiedCode(null), 2000);
      }}
      className="p-1 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
      title="Copy"
    >
      {copiedCode === id ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

/** Dark response card — variant: success | error | warning */
function ResponseCard({ title, variant = "success", body, id, copiedCode, setCopiedCode }) {
  const colors = {
    success: { dot: "bg-green-400", title: "text-green-400", border: "border-green-500/20" },
    error: { dot: "bg-red-400", title: "text-red-400", border: "border-red-500/20" },
    warning: { dot: "bg-amber-400", title: "text-amber-400", border: "border-amber-500/20" },
  }[variant] || { dot: "bg-green-400", title: "text-green-400", border: "border-green-500/20" };

  const json = typeof body === "string" ? body : JSON.stringify(body, null, 2);

  return (
    <div className={`rounded-xl bg-[#0f172a] border ${colors.border} overflow-hidden`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
          <span className={`text-[11px] font-bold uppercase tracking-wide ${colors.title}`}>{title}</span>
        </div>
        <CopyBtn text={json} id={id} copiedCode={copiedCode} setCopiedCode={setCopiedCode} />
      </div>
      <pre className="p-3 text-[11px] leading-relaxed text-gray-200 overflow-x-auto max-h-64">
        <code>{json}</code>
      </pre>
    </div>
  );
}

function MethodBadge({ method }) {
  const map = {
    GET: "bg-green-100 text-green-800",
    POST: "bg-blue-100 text-blue-800",
    PUT: "bg-amber-100 text-amber-800",
    PATCH: "bg-amber-100 text-amber-800",
    DELETE: "bg-red-100 text-red-800",
  };
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${map[method] || "bg-gray-100 text-gray-700"}`}>
      {method}
    </span>
  );
}

function EndpointBlock({ method, path, auth, description, query, requestBody, success, error, warning, idPrefix, copiedCode, setCopiedCode }) {
  return (
    <div className="space-y-3 pb-5 border-b border-gray-100 last:border-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-2">
        <MethodBadge method={method} />
        <code className="text-xs font-mono text-gray-900">{path}</code>
        {auth === "Public" ? (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 font-semibold">Public</span>
        ) : (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">API Key</span>
        )}
      </div>
      {description && <p className="text-xs text-gray-500">{description}</p>}

      {query && (
        <div>
          <p className="text-[11px] font-semibold text-gray-700 mb-1">Query parameters</p>
          <pre className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 text-[11px] text-gray-800 overflow-x-auto">{query}</pre>
        </div>
      )}

      {requestBody && (
        <div>
          <p className="text-[11px] font-semibold text-gray-700 mb-1">Request body</p>
          <pre className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 text-[11px] text-gray-800 overflow-x-auto">{typeof requestBody === "string" ? requestBody : JSON.stringify(requestBody, null, 2)}</pre>
        </div>
      )}

      <div className={`grid gap-3 ${warning ? "grid-cols-1 lg:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
        {success && (
          <ResponseCard
            title="Success Response (JSON)"
            variant="success"
            body={success}
            id={`${idPrefix}-ok`}
            copiedCode={copiedCode}
            setCopiedCode={setCopiedCode}
          />
        )}
        {warning && (
          <ResponseCard
            title="Success but Failed"
            variant="warning"
            body={warning}
            id={`${idPrefix}-warn`}
            copiedCode={copiedCode}
            setCopiedCode={setCopiedCode}
          />
        )}
        {error && (
          <ResponseCard
            title="Failed Response (JSON)"
            variant="error"
            body={error}
            id={`${idPrefix}-err`}
            copiedCode={copiedCode}
            setCopiedCode={setCopiedCode}
          />
        )}
      </div>
    </div>
  );
}

export default function ApiKeys() {
  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [oneTimeKey, setOneTimeKey] = useState(null); // {name, value}
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, name}
  const location = useLocation();
  const navigate = useNavigate();
  const [docsExpanded, setDocsExpanded] = useState(
    !!location.state?.openDocs || location.hash === "#api-reference"
  );
  const [copiedCode, setCopiedCode] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { organization } = useOrganization();

  // Open endpoint reference when navigated from API Guide (state or hash)
  useEffect(() => {
    const shouldOpen =
      !!location.state?.openDocs || location.hash === "#api-reference";
    if (!shouldOpen) return;

    setDocsExpanded(true);

    // Scroll after paint so the expanded panel is in the DOM
    const t = setTimeout(() => {
      document.getElementById("api-reference")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);

    // Clear navigation state so browser Back does not re-trigger open
    if (location.state?.openDocs) {
      navigate(location.pathname + (location.hash || ""), { replace: true, state: {} });
    }

    return () => clearTimeout(t);
  }, [location.state?.openDocs, location.hash, location.pathname, navigate]);

  useEffect(() => {
    localStorage.setItem('loading', loading);
  }, [loading])

  useEffect(() => {
    const handleScroll = () => {
      // Show button when scrolled more than 300px
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Cleanup
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const fetchKeys = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getApiKeys();
      const list = data?.keys || data?.data || data || [];
      setKeys(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const onCreate = async (e) => {
    e?.preventDefault?.();
    if (!newKeyName.trim()) {
      toast.error("Enter a key name");
      return;
    }
    setCreateSubmitting(true);
    try {
      const res = await ApiService.createApiKey(newKeyName.trim());
      // Expect the full key value only once in response
      const keyValue = res?.value || res?.key || res?.data?.value || null;
      if (keyValue) {
        setOneTimeKey({ name: newKeyName.trim(), value: keyValue });
        toast.success("API key created. Copy and store it securely.");
      } else {
        toast.success("API key created");
      }
      setCreateOpen(false);
      setNewKeyName("");
      fetchKeys();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to create API key");
    } finally {
      setCreateSubmitting(false);
    }
  };

  const onDeleteClick = (keyObj) => {
    const id = keyObj?.key_id || keyObj?.id || keyObj?._id;
    const name = keyObj?.key_name || keyObj?.name || "this API key";
    if (!id) {
      toast.error("Missing key id");
      return;
    }
    setDeleteConfirm({ id, name });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    setDeletingId(deleteConfirm.id);
    try {
      await ApiService.deleteApiKey(String(deleteConfirm.id));
      toast.success("API key deleted successfully");
      setKeys((prev) => prev.filter((k) => (k.key_id || k.id || k._id) !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to delete API key");
    } finally {
      setDeletingId(null);
    }
  };

  const emptyState = !loading && (!keys || keys.length === 0);

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent leading-tight">
            API Keys
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Generate and manage API keys for integrating with your site.</p>
          {organization && (
            <div className="mt-2 flex items-center gap-2 bg-gray-50 rounded px-2 py-1.5 border border-gray-200 max-w-[350px]">
              <span className="text-[10px] text-gray-400 font-mono truncate flex-1 uppercase">
                Org ID: {organization.id}
              </span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(organization.id);
                  toast.success("Organization ID copied!")
                }}
                className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
                title="Copy Organization ID"
              >
                <Copy className="h-3 w-3 text-gray-400" />
              </button>
            </div>
          )}
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex h-10 items-center justify-center px-4 bg-black text-white text-sm font-bold rounded-lg shadow-sm hover:bg-gray-800 transition-all"
        >
          <Plus className="h-4 w-4 mr-2" /> <span>Create Key</span>
        </button>
      </div>

      {/* Getting started banner */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm sm:text-base font-bold text-gray-900">New to the API? Start here</h3>
          <ol className="mt-2 text-xs sm:text-sm text-gray-600 list-decimal pl-5 space-y-0.5">
            <li>Create a key and copy it (it&apos;s shown only once).</li>
            <li>Send it in the <code className="bg-gray-100 px-1 rounded">X-API-KEY</code> header from your server.</li>
            <li>Follow the guide for examples in cURL, JavaScript, Python and PHP.</li>
          </ol>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 lg:flex-shrink-0">
          <button
            onClick={() => navigate("/api-guide")}
            className="inline-flex h-10 items-center justify-center px-4 bg-black text-white text-sm font-bold rounded-lg shadow-sm hover:bg-gray-800 transition-all"
          >
            <BookOpen className="h-4 w-4 mr-2" /> Integration guide
          </button>
          <button
            onClick={() => {
              setDocsExpanded(true);
              setTimeout(() => document.getElementById("api-reference")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
            }}
            className="inline-flex h-10 items-center justify-center px-4 border border-gray-300 text-gray-800 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-all"
          >
            Endpoint reference
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-[90vh]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black" />
        </div>
      ) : emptyState ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-gray-50 flex items-center justify-center mb-3">
            <Lock className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">No API keys yet</h3>
          <p className="mt-1 text-sm text-gray-500">Create your first API key, then follow the integration guide to make your first request.</p>
          <button
            onClick={() => setCreateOpen(true)}
            className="mt-5 inline-flex h-10 items-center px-5 bg-black text-white rounded-lg text-sm font-bold shadow-sm hover:bg-gray-800 transition-all"
          >
            <Plus className="h-4 w-4 mr-2" /> Create API key
          </button>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-5 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-5 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Created at</th>
                  <th className="px-5 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Value</th>
                  <th className="px-5 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Expires at</th>
                  <th className="px-5 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {keys.map((k) => {
                  const id = k?.key_id || k?.id || k?._id || undefined;
                  const createdDisplay = formatDate(
                    k?.created_at || k?.createdOn || k?.created || k?.createdAt || new Date().toISOString()
                  );
                  return (
                    <tr key={id || k?.name} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3 text-sm text-gray-900 font-bold">{k?.key_name || k?.name || "-"}</td>
                      <td className="px-5 py-3 text-xs text-gray-500">{createdDisplay}</td>
                      <td className="px-5 py-3 text-sm font-mono text-gray-500 max-w-[160px] truncate">
                        <span className="bg-gray-50 px-2 py-0.5 rounded border border-gray-100">{maskValue(k)}</span>
                      </td>
                      <td className="px-5 py-3 text-xs text-gray-500">{formatDate(k?.expires_at || k?.expiresAt)}</td>
                      <td className="px-5 py-3 text-right">
                        <button
                          onClick={() => onDeleteClick(k)}
                          disabled={!id || deletingId === id}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50"
                          title="Delete key"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {keys.map((k) => {
              const id = k?.key_id || k?.id || k?._id || undefined;
              const createdDisplay = formatDate(
                k?.created_at || k?.createdOn || k?.created || k?.createdAt || new Date().toISOString()
              );
              return (
                <div key={id || k?.name} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900">{k?.key_name || k?.name || "-"}</h3>
                      <p className="text-xs text-gray-500 mt-1">Created {createdDisplay}</p>
                    </div>
                    <button
                      onClick={() => onDeleteClick(k)}
                      disabled={!id || deletingId === id}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:text-red-600 hover:border-red-300 hover:bg-red-50 disabled:opacity-50 flex-shrink-0"
                      title="Delete key"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2 border-t border-gray-100">
                      <span className="text-xs font-medium text-gray-600 uppercase">Value</span>
                      <span className="text-sm font-mono text-gray-900">{maskValue(k)}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-t border-gray-100">
                      <span className="text-xs font-medium text-gray-600 uppercase">Expires</span>
                      <span className="text-sm text-gray-700">{formatDate(k?.expires_at || k?.expiresAt)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* API Documentation Section */}
      <div id="api-reference" className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm scroll-mt-4">
        <button
          onClick={() => setDocsExpanded(!docsExpanded)}
          className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-gray-50/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-black rounded-lg">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <div className="text-left">
              <h3 className="text-sm sm:text-base font-bold text-gray-900">API Documentation</h3>
              <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider font-semibold">Implementation examples</p>
            </div>
          </div>
          {docsExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </button>

        {docsExpanded && (
          <div className="border-t border-gray-200 p-4 sm:p-6 space-y-8 max-h-[75vh] overflow-y-auto">

            {/* Auth */}
            <section>
              <h4 className="text-sm font-bold text-gray-900 mb-2">1. Authentication</h4>
              <p className="text-xs sm:text-sm text-gray-600 mb-3">
                Authenticated endpoints require the <code className="bg-gray-100 px-1 rounded text-xs">X-API-KEY</code> header.
                Organization is resolved from the key automatically — do not send X-Organization-ID or X-User-ID.
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs font-mono text-gray-800 space-y-1">
                <div>Base URL: <span className="text-blue-700">https://api.uttik.com</span></div>
                <div>Header: <span className="text-blue-700">X-API-KEY: utsk_your_key_here</span></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Your org ID (for public endpoints): <code className="bg-gray-100 px-1 rounded break-all">{organization?.id || '—'}</code></p>
            </section>

            {/* FAQs */}
            <section className="space-y-1">
              <h4 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2 mb-4">2. FAQs</h4>

              <EndpointBlock
                method="GET" path="/api/faqs/list" auth="API Key"
                description="List FAQs for your organization (paginated, searchable)."
                query={`page      integer  default 1\nper_page  integer  default 10, max 100\nsearch    string   optional`}
                success={{
                  faqs: [{
                    faq_id: "03e03b1a-a5bc-44bc-9d63-1929e6e7828e",
                    organization_id: "org_…",
                    question: "How do I reset my password?",
                    answer: "Go to Settings → Security → Reset Password.",
                    slug: "how-do-i-reset-my-password",
                    status: "public",
                    tags: ["account", "password"],
                    category_ids: [],
                    confidence: 0.8,
                    created_at: "2026-01-15T10:30:00.000Z",
                    updated_at: "2026-01-15T10:30:00.000Z"
                  }],
                  pagination: { current_page: 1, per_page: 10, total_items: 42, total_pages: 5 }
                }}
                error={{ success: false, message: "Error listing FAQs: …" }}
                idPrefix="faq-list" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="POST" path="/api/faqs/create" auth="API Key"
                description="Create a new FAQ. Returns 409 if a similar question already exists."
                requestBody={{
                  question: "How do I reset my password?",
                  answer: "Go to Settings → Security → Reset Password.",
                  status: "public",
                  tags: ["account", "password"],
                  categories: [],
                  slug: null,
                  author: null
                }}
                success={{
                  success: true,
                  message: "FAQ created successfully",
                  faq: {
                    faq_id: "uuid",
                    organization_id: "org_…",
                    question: "How do I reset my password?",
                    answer: "Go to Settings → Security → Reset Password.",
                    slug: "how-do-i-reset-my-password-a1b2c3",
                    status: "public",
                    tags: ["account", "password"],
                    created_at: "2026-01-15T10:30:00.000Z"
                  }
                }}
                error={{ success: false, error_code: "FAQ_DUPLICATE", message: "A FAQ with a similar question already exists for this organization." }}
                idPrefix="faq-create" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="PUT" path="/api/faqs/update" auth="API Key"
                description="Update an existing FAQ. Only send fields you want to change."
                requestBody={{
                  faq_id: "uuid",
                  question: null,
                  answer: "Updated answer text.",
                  status: "public",
                  tags: null,
                  categories: null,
                  needs_review: false
                }}
                success={{ success: true, message: "FAQ updated successfully" }}
                error={{ success: false, error_code: "FAQ_NOT_FOUND", message: "FAQ not found" }}
                idPrefix="faq-update" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="DELETE" path="/api/faqs/delete" auth="API Key"
                description="Delete one or more FAQs by ID."
                requestBody={{ faqId_list: ["uuid-1", "uuid-2"] }}
                success={{ success: true, message: "FAQs deleted successfuly!" }}
                error={{ success: false, message: "Specified FAQs not found" }}
                idPrefix="faq-delete" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="POST" path="/api/faqs/optimize" auth="API Key"
                description="AI-optimize FAQ answers in batch (max 50)."
                requestBody={{
                  faq_data_list: [{
                    faq_id: "uuid",
                    optimization_type: "simplify",
                    question: "How do I reset my password?",
                    answer: "Long original answer…"
                  }]
                }}
                success={{
                  success: true,
                  result: { faq_id: ["uuid"], optimized_answers: ["Optimized text…"] },
                  error: []
                }}
                error={{ success: false, message: "Batch size limit exceeded" }}
                idPrefix="faq-opt" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="POST" path="/api/faqs/bulk-update" auth="API Key"
                description="Bulk actions: set_status, add_tag, remove_tag, auto_tag, optimize_text, auto_link."
                requestBody={{
                  faq_ids: ["uuid-1", "uuid-2"],
                  actions: [
                    { type: "set_status", value: "public" },
                    { type: "add_tag", value: "shipping" }
                  ]
                }}
                success={{ successful: ["uuid-1", "uuid-2"], failed: [], errors: [] }}
                error={{ successful: [], failed: ["uuid-1"], errors: [{ faq_id: "uuid-1", error: "FAQ not found" }] }}
                idPrefix="faq-bulk" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="GET" path="/api/faqs/get/public" auth="Public"
                description="Public FAQ list for widgets. Optionally scored by client_url."
                query={`organization_id  string  required\nclient_url       string  optional`}
                success={{
                  faqs: [{ faq_id: "uuid", question: "…", answer: "…", tags: [], score: 12 }]
                }}
                error={{ success: false, error_code: "FAQ_LIST_PUBLIC_FAILED", message: "Error listing FAQs: …" }}
                idPrefix="faq-pub" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="GET" path="/api/faqs/get/public/widget" auth="Public"
                description="Paginated public FAQs with search and scoring."
                query={`organization_id  string   required\npage             integer  default 1\nper_page         integer  default 10, max 100\nsearch           string   optional\ntag              string   optional\nclient_url       string   optional`}
                success={{
                  faqs: [{ faq_id: "uuid", question: "…", answer: "…", tags: [], score: 5 }],
                  pagination: { current_page: 1, per_page: 10, total_items: 20, total_pages: 2 }
                }}
                error={{ success: false, message: "Error listing FAQs: …" }}
                idPrefix="faq-widget" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="GET" path="/api/faqs/{slug}" auth="Public"
                description="Get a single FAQ by its slug."
                query={`id  string  optional organization_id`}
                success={{
                  faq_id: "uuid",
                  question: "How do I reset my password?",
                  answer: "Go to Settings → Security → Reset Password.",
                  slug: "how-do-i-reset-my-password",
                  status: "public",
                  tags: ["account"]
                }}
                error={{ success: false, message: "FAQ not found" }}
                idPrefix="faq-slug" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="GET" path="/api/faqs/sitemap" auth="Public"
                description="FAQ sitemap XML for SEO."
                query={`org_id  string  required`}
                success={`<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url><loc>https://…/faqs/how-do-i-reset-my-password</loc></url>
</urlset>`}
                error={{ success: false, message: "Organization not found" }}
                idPrefix="faq-sitemap" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="POST" path="/api/faqs/feedback" auth="Public"
                description="Submit helpful / not helpful feedback for an FAQ."
                requestBody={{ faq_id: "uuid", is_helpful: true }}
                success={{ success: true, message: "Feedback submitted successfully" }}
                error={{ success: false, message: "FAQ not found" }}
                idPrefix="faq-fb" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />
            </section>

            {/* Chat */}
            <section className="space-y-1">
              <h4 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2 mb-4">3. Chat</h4>
              <EndpointBlock
                method="POST" path="/api/chat/query/public" auth="Public"
                description="AI chat with RAG. Used by the chat widget."
                requestBody={{
                  organization_id: organization?.id || "org_…",
                  query: "How do I reset my password?",
                  chat_history: [],
                  shop: null
                }}
                success={{
                  response: "To reset your password, go to Settings → Security → Reset Password.",
                  suggested_questions: ["How do I change my email?", "How do I enable 2FA?"],
                  confidence: 0.85
                }}
                error={{ success: false, message: "Organization not found" }}
                idPrefix="chat" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />
            </section>

            {/* Support */}
            <section className="space-y-1">
              <h4 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2 mb-4">4. Support</h4>

              <EndpointBlock
                method="GET" path="/api/support/queries" auth="API Key"
                description="List support tickets for your organization."
                query={`limit  integer  default 30, 0 = all`}
                success={{
                  queries: [{
                    query_id: "550e8400-e29b-41d4-a716-446655440000",
                    ticket_id: "UTTIK-2025-001234",
                    organization_id: "org_…",
                    name: "John Doe",
                    email: "john@example.com",
                    contact_number: "+1-234-567-8900",
                    message: "I need help with the widget.",
                    source_url: "https://example.com/help",
                    status: "open",
                    priority: "medium",
                    created_at: "2025-11-15T14:30:22.123Z",
                    updated_at: "2025-11-15T14:30:22.123Z"
                  }]
                }}
                error={{ success: false, error_code: "SUPPORT_QUERY_LIST_FAILED", message: "Error getting support queries: …" }}
                idPrefix="sup-list" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="GET" path="/api/support/queries/{query_id}" auth="API Key"
                description="Get a single support ticket by ID."
                success={{
                  query_id: "550e8400-e29b-41d4-a716-446655440000",
                  ticket_id: "UTTIK-2025-001234",
                  name: "John Doe",
                  email: "john@example.com",
                  message: "I need help with the widget.",
                  status: "open",
                  priority: "medium",
                  notes: "",
                  created_at: "2025-11-15T14:30:22.123Z"
                }}
                error={{ success: false, message: "Support query not found" }}
                idPrefix="sup-get" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="POST" path="/api/support/submit/public" auth="Public"
                description="Submit a support ticket (also POST /api/widget/support). Extra fields are stored in additional_data."
                requestBody={{
                  organization_id: organization?.id || "org_…",
                  customer_email: "customer@example.com",
                  query: "I need help with my order",
                  name: "Jane Doe",
                  mobile: "+1-555-0199",
                  source_url: "https://store.com/checkout"
                }}
                success={{
                  success: true,
                  query_id: "550e8400-e29b-41d4-a716-446655440000",
                  auto_response: true,
                  email_sent: true,
                  admin_notified: true
                }}
                error={{ success: false, message: "Bad Request - Customer email, query and some other fields are mandatory." }}
                idPrefix="sup-submit" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="PATCH" path="/api/support/update/status" auth="API Key"
                description="Batch update status and/or priority on support tickets."
                requestBody={{
                  support_queryIds: ["uuid-1", "uuid-2"],
                  status: "responded",
                  priority: "high"
                }}
                success={{
                  success: true,
                  modified_count: 2,
                  matched_count: 2,
                  message: "Updated 2 queries"
                }}
                error={{ success: false, message: "support_queryIds and status or priority are required" }}
                idPrefix="sup-status" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />
            </section>

            {/* Widgets */}
            <section className="space-y-1">
              <h4 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2 mb-4">5. Widgets</h4>

              <EndpointBlock
                method="GET" path="/api/widgets/list" auth="API Key"
                description="List all widgets for your organization."
                success={{
                  success: true,
                  widgets: [{
                    widget_id: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                    organization_id: "org_…",
                    widget_type: "faq",
                    configuration: {
                      title: "Frequently Asked Questions",
                      primaryColor: "#3B82F6",
                      secondaryColor: "#6366F1",
                      supportEnabled: true,
                      contactPosition: "bottom",
                      accordionIconStyle: "plus-minus"
                    },
                    embed_code: "<script>…</script>",
                    created_at: "2025-11-15T10:30:00.000Z"
                  }]
                }}
                error={{ success: false, message: "Authentication required" }}
                idPrefix="wid-list" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="GET" path="/api/widgets/{widget_id}" auth="API Key"
                description="Get a single widget by ID."
                success={{
                  success: true,
                  widget: {
                    widget_id: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                    widget_type: "faq",
                    configuration: { title: "Help Center", primaryColor: "#3B82F6" },
                    embed_code: "<script>…</script>",
                    created_at: "2025-11-15T10:30:00.000Z"
                  }
                }}
                error={{ success: false, message: "Widget not found" }}
                idPrefix="wid-get" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="GET" path="/api/widgets/{widget_id}/config" auth="API Key"
                description="Get widget configuration for real-time updates."
                success={{
                  title: "Help Center",
                  primaryColor: "#3B82F6",
                  secondaryColor: "#6366F1",
                  supportEnabled: true
                }}
                error={{ success: false, message: "Widget not found" }}
                idPrefix="wid-cfg" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="POST" path="/api/widgets/create" auth="API Key"
                description="Create a new FAQ or Chat widget."
                requestBody={{
                  widget_type: "faq",
                  configuration: {
                    title: "Frequently Asked Questions",
                    primaryColor: "#3B82F6",
                    secondaryColor: "#6366F1",
                    supportEnabled: true
                  }
                }}
                success={{
                  success: true,
                  widget: {
                    widget_id: "uuid",
                    organization_id: "org_…",
                    widget_type: "faq",
                    configuration: { title: "Frequently Asked Questions", primaryColor: "#3B82F6" },
                    embed_code: "<script>…</script>",
                    created_at: "2025-11-15T10:30:00.000Z"
                  }
                }}
                error={{ success: false, error_code: "WIDGET_CREATION_FAILED", message: "Widget creation failed: …" }}
                idPrefix="wid-create" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="PUT" path="/api/widgets/{widget_id}/settings" auth="API Key"
                description="Update widget settings / configuration (partial)."
                requestBody={{ title: "Updated Title", primaryColor: "#10B981" }}
                success={{ success: true, widget: { widget_id: "uuid", configuration: { title: "Updated Title", primaryColor: "#10B981" } } }}
                error={{ success: false, message: "Widget not found" }}
                idPrefix="wid-set" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="DELETE" path="/api/widgets/{widget_id}" auth="API Key"
                description="Delete a widget."
                success={{ success: true, message: "Widget deleted successfully" }}
                error={{ success: false, message: "Widget not found" }}
                idPrefix="wid-del" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />
            </section>

            {/* Analytics & Org */}
            <section className="space-y-1">
              <h4 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2 mb-4">6. Analytics & Organization</h4>

              <EndpointBlock
                method="GET" path="/api/analytics/overview" auth="API Key"
                description="Aggregated analytics overview for the organization."
                query={`start_date  ISO date  optional (default: 30 days ago)\nend_date    ISO date  optional (default: today)`}
                success={{
                  success: true,
                  message: "Analytics overview fetched successfully",
                  analytics: {
                    date_range: { start_date: "2025-06-01T00:00:00", end_date: "2025-11-01T00:00:00" },
                    query_technicals: { top_languages: ["english"], avg_char_length: 92, avg_word_count: 20 },
                    ai_response_quality: { avg_consolidated_ai_response_quality_score: 83 },
                    rag_performance: { top_knowledge_gap_categories: ["technical_troubleshooting"], avg_rag_performance_score: 61 },
                    root_cause: { top_root_causes: ["product_service_failure"], avg_root_cause_confidence: 0.84 },
                    urgency: { avg_urgency_score: 44 },
                    sentiment: { top_sentiments: ["neutral"], avg_sentiment_score: -1 },
                    classification: { top_primary_categories: ["product_issues"] }
                  }
                }}
                error={{ success: false, message: "Error fetching analytics overview: …" }}
                idPrefix="an-ov" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="GET" path="/api/org/" auth="API Key"
                description="Get organization details."
                success={{
                  organization_id: "org_…",
                  organization_name: "Acme Corporation",
                  organization_domain: "https://acmecorp.com",
                  organization_email: "support@acmecorp.com",
                  organization_context: "Acme is a leading technology company…",
                  custom_prompt: "Always respond professionally.",
                  created_at: "2025-11-14T10:30:00.000Z"
                }}
                error={{ success: false, message: "Organization not found" }}
                idPrefix="org-get" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />
            </section>

            {/* Categories & Tags */}
            <section className="space-y-1">
              <h4 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2 mb-4">7. Categories & Tags</h4>

              <EndpointBlock
                method="GET" path="/api/categories/" auth="API Key"
                description="List all categories for the organization."
                success={{
                  success: true,
                  categories: [{ category_id: "uuid", name: "Shipping", description: "Shipping related FAQs", organization_id: "org_…" }],
                  total: 1
                }}
                error={{ success: false, message: "Error fetching categories: …" }}
                idPrefix="cat-list" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="POST" path="/api/categories/create" auth="API Key"
                description="Create a category (max 50 per org)."
                requestBody={{ name: "Shipping", description: "Shipping related FAQs" }}
                success={{
                  success: true,
                  category: { category_id: "uuid", name: "Shipping", description: "Shipping related FAQs", organization_id: "org_…" }
                }}
                error={{ success: false, message: "Category name is required" }}
                idPrefix="cat-create" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="GET" path="/api/tags/list" auth="API Key"
                description="List all tags used across FAQs."
                success={{
                  success: true,
                  tags: [{ name: "shipping", count: 12, created_at: null }]
                }}
                error={{ success: false, message: "Error listing tags: …" }}
                idPrefix="tag-list" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />

              <EndpointBlock
                method="POST" path="/api/tags/create" auth="API Key"
                description="Create a tag (stored lowercase)."
                requestBody={{ tag_name: "shipping" }}
                success={{
                  success: true,
                  message: "Tag created successfully",
                  tag: { name: "shipping", count: 0 }
                }}
                error={{ success: false, message: "Tag name cannot be empty" }}
                idPrefix="tag-create" copiedCode={copiedCode} setCopiedCode={setCopiedCode}
              />
            </section>

            {/* JS helper */}
            <section className="space-y-3">
              <h4 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2">8. JavaScript helper</h4>
              <div className="rounded-xl bg-[#0f172a] border border-white/5 overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Reusable fetch wrapper</span>
                  <CopyBtn
                    text={`const BASE_URL = 'https://api.uttik.com';\nconst API_KEY = 'utsk_your_key_here';\n\nasync function uttik(path, { method = 'GET', body } = {}) {\n  const res = await fetch(\`\${BASE_URL}\${path}\`, {\n    method,\n    headers: {\n      'X-API-KEY': API_KEY,\n      'Content-Type': 'application/json'\n    },\n    body: body ? JSON.stringify(body) : undefined\n  });\n  if (!res.ok) throw new Error(await res.text());\n  return res.json();\n}`}
                    id="ex-js"
                    copiedCode={copiedCode}
                    setCopiedCode={setCopiedCode}
                  />
                </div>
                <pre className="p-3 text-[11px] leading-relaxed text-gray-200 overflow-x-auto">
                  <code>{`const BASE_URL = 'https://api.uttik.com';
const API_KEY = 'utsk_your_key_here';

async function uttik(path, { method = 'GET', body } = {}) {
  const res = await fetch(\`\${BASE_URL}\${path}\`, {
    method,
    headers: {
      'X-API-KEY': API_KEY,
      'Content-Type': 'application/json'
    },
    body: body ? JSON.stringify(body) : undefined
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}`}</code>
                </pre>
              </div>
            </section>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 space-y-1">
              <p className="text-xs text-gray-700"><strong>API Key auth:</strong> <code className="bg-gray-100 px-1 rounded">X-API-KEY: utsk_…</code> — org inferred from key.</p>
              <p className="text-xs text-gray-700"><strong>Public endpoints:</strong> pass <code className="bg-gray-100 px-1 rounded">organization_id</code> only; no key needed.</p>
              <p className="text-xs text-gray-500">Keys expire after 365 days. Never expose them in client-side code for privileged operations.</p>
            </div>
          </div>
        )}
      </div>

      {createOpen && (
        <div className="fixed inset-0 z-[1000] bg-black/50 backdrop-blur-sm flex items-center justify-center !mt-0" onClick={() => setCreateOpen(false)}>
          <div className="relative mx-auto max-w-md w-full bg-white rounded-xl shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold">Create API key</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Provide a friendly name to identify this key.</p>
            <form onSubmit={onCreate}>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400"
                placeholder="e.g., production-site"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
              />
              <div className="mt-4 flex justify-end gap-2">
                <button type="button" onClick={() => setCreateOpen(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700">Cancel</button>
                <button type="submit" disabled={createSubmitting} className="px-4 py-2 rounded-lg bg-black text-white hover:bg-gray-800 disabled:opacity-50">
                  {createSubmitting ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {oneTimeKey && (
        <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center !mt-0" onClick={() => setOneTimeKey(null)}>
          <div className="relative mx-auto max-w-lg w-full bg-white rounded-xl shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-shrink-0">
                <div className="w-9 h-9 rounded-lg bg-amber-100 border border-amber-200 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Copy your API key now</h3>
                <p className="text-sm text-gray-700 mt-1">
                  This value will be shown <strong>only once</strong>. Store it in a secure password manager.
                </p>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-sm flex items-center justify-between">
              <span className="truncate mr-3">{oneTimeKey.value}</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(oneTimeKey.value);
                  toast.success("Copied");
                }}
                className="inline-flex items-center px-3 py-1.5 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <Copy className="h-4 w-4 mr-1" /> Copy
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setOneTimeKey(null)} className="px-4 py-2 rounded-lg bg-black text-white hover:bg-gray-800">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[1000] bg-black/50 backdrop-blur-sm flex items-center justify-center !mt-0" onClick={() => setDeleteConfirm(null)}>
          <div className="relative mx-auto max-w-md w-full bg-white rounded-xl shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-lg bg-red-100 border border-red-200 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Delete API Key?</h3>
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete <strong className="text-gray-900">&quot;{deleteConfirm.name}&quot;</strong>? This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-800 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>Any applications using this API key will immediately lose access.</span>
              </p>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deletingId === deleteConfirm.id}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingId === deleteConfirm.id ? 'Deleting...' : 'Delete API Key'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 p-3 bg-black text-white rounded-full shadow-lg hover:bg-gray-800 transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
          aria-label="Scroll to top"
        >
          <ArrowUp className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}
